<div class="pb-shell pb-detail" [attr.data-theme]="data.theme">
  <header class="mb-5 flex items-start justify-between gap-4">
    <div class="min-w-0">
      <p class="pb-eyebrow">
        {{ data.row.record_kind === 'package' ? 'Package contents' : 'Product details' }}
      </p>
      <h2>{{ data.row.model || data.row.description || data.row.barcode || 'Product' }}</h2>
      <p class="pb-muted text-sm">{{ data.row.customer_name }}</p>
    </div>
    <div class="flex flex-wrap gap-2">
      <span
        [title]="
          detail?.record?.package_uuid || data.row.package_uuid
            ? 'Preview this package label'
            : 'A package is required to print a label'
        "
      >
        <button
          type="button"
          class="pb-button pb-primary"
          (click)="showPackageLabel()"
          [disabled]="!(detail?.record?.package_uuid || data.row.package_uuid)"
        >
          <i class="fa-solid fa-print" aria-hidden="true"></i>
          Print Label
        </button>
      </span>
      <button type="button" class="pb-button" mat-dialog-close aria-label="Close product details">
        Close
      </button>
    </div>
  </header>
  <p *ngIf="loading" role="status" class="pb-muted">Loading details…</p>
  <p *ngIf="error" role="alert" class="pb-alert">
    {{ error }}
    <button class="underline" (click)="load()">Retry</button>
  </p>
  <ng-container *ngIf="detail as d">
    <div *ngIf="d.record.customer_conflict" class="pb-alert mb-4">
      The product customer differs from its package customer:
      {{ d.record.package_customer_name || 'unnamed customer' }}. Both recorded assignments are
      preserved.
    </div>
    <div class="grid gap-4 md:grid-cols-2">
      <section class="pb-panel rounded-xl p-4">
        <h3>Product</h3>
        <dl>
          <div *ngFor="let field of fields">
            <dt>{{ field.label }}</dt>
            <dd>{{ value(d.record[field.key]) }}</dd>
          </div>
        </dl>
      </section>
      <div class="space-y-4">
        <section class="pb-panel rounded-xl p-4">
          <h3>Current recorded location</h3>
          <dl>
            <div>
              <dt>Warehouse</dt>
              <dd>{{ d.record.warehouse_name || 'No current spot recorded' }}</dd>
            </div>
            <div>
              <dt>Spot</dt>
              <dd>{{ d.record.spot || '—' }}</dd>
            </div>
            <div>
              <dt>Assigned since</dt>
              <dd>{{ date(d.record.position_since) }}</dd>
            </div>
          </dl>
          <p class="pb-muted mt-3 text-xs">Based on the active package-to-spot assignment.</p>
        </section>
        <section class="pb-panel rounded-xl p-4">
          <h3>Customer and references</h3>
          <dl>
            <div>
              <dt>Customer</dt>
              <dd>{{ d.record.customer_name }}</dd>
            </div>
            <div>
              <dt>Customer source</dt>
              <dd>{{ d.record.customer_source }}</dd>
            </div>
            <div *ngFor="let field of references">
              <dt>{{ field.label }}</dt>
              <dd>{{ value(d.record[field.key]) }}</dd>
            </div>
            <div *ngIf="d.record.trip_uuid">
              <dt>Trip</dt>
              <dd>
                <a
                  [routerLink]="['/full-trip-details', d.record.trip_uuid]"
                  target="_blank"
                  rel="noopener"
                >
                  Open trip {{ d.record.trip_number }}
                </a>
              </dd>
            </div>
          </dl>
        </section>
        <section class="pb-panel rounded-xl p-4">
          <h3>Record</h3>
          <dl>
            <div>
              <dt>Created</dt>
              <dd>{{ date(d.record.created) }}</dd>
            </div>
            <div>
              <dt>Intake</dt>
              <dd>{{ date(d.record.intake_date) }}</dd>
            </div>
            <div>
              <dt>Available</dt>
              <dd>{{ flag(d.record.available) }}</dd>
            </div>
            <div>
              <dt>Delivered</dt>
              <dd>{{ flag(d.record.delivered) }}</dd>
            </div>
            <div>
              <dt>Product UUID</dt>
              <dd>{{ d.record.product_uuid || '—' }}</dd>
            </div>
            <div>
              <dt>Package UUID</dt>
              <dd>{{ d.record.package_uuid || 'Not palletized / no package association' }}</dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
    <section *ngIf="d.work_orders.length" class="pb-panel mt-4 rounded-xl p-4">
      <h3>Related work orders</h3>
      <div class="flex flex-wrap gap-3">
        <a
          *ngFor="let order of d.work_orders"
          [routerLink]="['/work_order_details', order.lading_uuid]"
          target="_blank"
          rel="noopener"
          class="text-sm"
        >
          {{ order.reference || 'Open work order' }} · {{ order.status || 'No status' }}
        </a>
      </div>
      <p *ngIf="d.work_orders_more" class="pb-muted mt-2 text-xs">
        Showing the latest 100 linked work orders.
      </p>
    </section>
    <section *ngIf="d.contents.length > 1 || d.contents_more" class="pb-panel mt-4 rounded-xl p-4">
      <h3>Products on this package / pallet</h3>
      <div class="overflow-auto">
        <table>
          <thead>
            <tr>
              <th>Model</th>
              <th>Serial</th>
              <th>Barcode</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of d.contents">
              <td>{{ item.model }}</td>
              <td>{{ item.serial }}</td>
              <td>{{ item.barcode }}</td>
              <td>{{ item.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p *ngIf="d.contents_more" class="pb-muted mt-2 text-xs">
        Showing the first 100 products on this package.
      </p>
    </section>
    <section class="pb-panel mt-4 rounded-xl p-4">
      <h3>Recorded product history</h3>
      <p *ngIf="!d.history.length" class="pb-muted text-sm">No product history entries recorded.</p>
      <dl class="space-y-3">
        <div *ngFor="let event of d.history">
          <dt>{{ date(event.recorded_at) }} · {{ event.event_type }}</dt>
          <dd>{{ event.reason || event.source }}</dd>
        </div>
      </dl>
      <p *ngIf="d.history_more" class="pb-muted mt-2 text-xs">Showing the latest 100 entries.</p>
    </section>
  </ng-container>
</div>
